import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, Clock, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, ENQUIRY_CATEGORIES } from "@/data/products";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", category: "", quantity: "", city: "", message: "" };

const FIELD = "w-full border-b border-ink/20 bg-transparent py-3 font-body text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink/35 focus:border-gold";
const LABEL = "font-mono text-[10px] uppercase tracking-[0.25em] text-clay";

const Contact = ({ interest }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (interest) setForm((f) => ({ ...f, category: interest }));
  }, [interest]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const waEnquiryUrl = () => {
    const rows = [
      ["Name / Business", form.name], ["Phone / WhatsApp", form.phone], ["Email", form.email],
      ["Category", form.category], ["Quantity", form.quantity], ["Delivery", form.city], ["Requirement", form.message],
    ].filter(([, v]) => v.trim()).map(([k, v]) => `*${k}:* ${v.trim()}`);
    const text = ["Hi Praveen Textiles, new enquiry from your website:", "", ...rows].join("\n");
    return `https://wa.me/${CONTACT.waNumber}?text=${encodeURIComponent(text)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const url = waEnquiryUrl();
    const tab = window.open(url, "_blank");
    if (tab) tab.opener = null;
    try {
      await axios.post(`${API}/enquiries`, form).catch(() => null);
      if (!tab) window.location.href = url;
      toast.success("Opening WhatsApp with your enquiry.", {
        description: `Thank you${form.name ? `, ${form.name.split(" ")[0]}` : ""} — just tap send in WhatsApp and we'll reply the same day.`,
      });
      setForm({ ...EMPTY, category: interest || "" });
    } catch {
      toast.error("Could not send your enquiry.", {
        description: "Please try again in a moment, or reach us instantly on WhatsApp.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="px-6 pb-16 sm:px-12 sm:pb-28 lg:px-20">
      <div className="mx-auto grid max-w-7xl overflow-hidden border border-gold/25 lg:grid-cols-2">
        <div className="relative border-b border-ink/10 bg-white p-10 text-ink lg:border-b-0 lg:border-r sm:p-14">
          <Reveal>
            <p className="flex items-center gap-4 font-accent text-[11px] font-semibold tracking-[0.35em] text-crimson">
              <span className="h-px w-12 bg-crimson" /> GET IN TOUCH
            </p>
            <h2 className="mt-6 font-display text-3xl font-normal leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              Talk to us about
              <br />
              your <span className="italic text-crimson">order.</span>
            </h2>
            <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-clay sm:text-base">
              For rates, stock checks and orders, WhatsApp is the quickest route — most enquiries
              get a same-day reply.
            </p>
          </Reveal>

          <Reveal delay={0.12}>
            <a
              data-testid="contact-whatsapp-btn"
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-9 inline-flex items-center gap-3 rounded-full bg-[#1FA855] px-7 py-4 font-body text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#25D366]"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-4.5 w-4.5 h-[18px] w-[18px]" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              Chat on WhatsApp
            </a>
          </Reveal>

          <Reveal delay={0.2} className="mt-12 space-y-7">
            <div className="flex gap-4">
              <Phone size={17} className="mt-0.5 shrink-0 text-crimson" />
              <div className="space-y-3">
                {CONTACT.phones.map((p, i) => (
                  <div key={p.href}>
                    <a data-testid={`contact-phone-link-${i}`} href={p.href} className="font-body text-sm font-semibold hover:text-clay">
                      {p.display}
                    </a>
                    <p className="mt-1 text-xs font-light text-clay">{p.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-4">
              <Mail size={17} className="mt-0.5 shrink-0 text-crimson" />
              <div>
                <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="font-body text-sm font-semibold hover:text-clay">
                  {CONTACT.email}
                </a>
                <p className="mt-1 text-xs font-light text-clay">For rate lists and order records</p>
              </div>
            </div>
            <div className="flex gap-4">
              <MapPin size={17} className="mt-0.5 shrink-0 text-crimson" />
              <div>
                <p className="font-body text-sm font-semibold">Praveen Textiles</p>
                <p className="mt-1 max-w-xs text-xs font-light leading-relaxed text-clay">{CONTACT.address}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <Clock size={17} className="mt-0.5 shrink-0 text-crimson" />
              <div>
                <p className="font-body text-sm font-semibold">{CONTACT.hours}</p>
                <p className="mt-1 text-xs font-light text-clay">WhatsApp monitored beyond counter hours</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.28}>
            <div className="mt-12 overflow-hidden border border-ink/10">
              <iframe
                data-testid="contact-map"
                title="Praveen Textiles — Old Washermenpet, Chennai"
                src="https://www.google.com/maps?q=Praveen%20Textiles%2C%20Old%20Washermenpet%2C%20Chennai%20600021&z=16&output=embed"
                className="h-52 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <a
              data-testid="directions-link"
              href="https://maps.google.com/?q=Praveen%20Textiles%2C%20Old%20Washermenpet%2C%20Chennai%20600021"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-block font-mono text-[10px] uppercase tracking-[0.22em] text-crimson underline-offset-4 hover:underline"
            >
              Get Directions · Old Washermenpet
            </a>
          </Reveal>
        </div>

        <form data-testid="enquiry-form" onSubmit={submit} className="grid grid-cols-1 content-center gap-x-8 gap-y-7 bg-parchment p-10 sm:grid-cols-2 sm:p-14">
          <div>
            <label htmlFor="enq-name" className={LABEL}>Full / Business Name *</label>
            <input id="enq-name" data-testid="input-name" required value={form.name} onChange={set("name")} placeholder="Meena Fabrics, Madurai" className={FIELD} />
          </div>
          <div>
            <label htmlFor="enq-phone" className={LABEL}>Phone / WhatsApp *</label>
            <input id="enq-phone" data-testid="input-phone" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ·····" className={FIELD} />
          </div>
          <div>
            <label htmlFor="enq-email" className={LABEL}>Email Address</label>
            <input id="enq-email" data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="you@business.com" className={FIELD} />
          </div>
          <div className="relative">
            <label htmlFor="enq-category" className={LABEL}>Category of Interest</label>
            <select id="enq-category" data-testid="select-category" value={form.category} onChange={set("category")} className={`${FIELD} appearance-none pr-8`}>
              <option value="" disabled>Select a category</option>
              {ENQUIRY_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute bottom-4 right-1 text-clay" />
          </div>
          <div>
            <label htmlFor="enq-quantity" className={LABEL}>Estimated Quantity</label>
            <input id="enq-quantity" data-testid="input-quantity" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 200 pieces / 500 m" className={FIELD} />
          </div>
          <div>
            <label htmlFor="enq-city" className={LABEL}>Delivery City & State</label>
            <input id="enq-city" data-testid="input-city" value={form.city} onChange={set("city")} placeholder="Coimbatore, TN" className={FIELD} />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="enq-message" className={LABEL}>Your Requirement</label>
            <textarea id="enq-message" data-testid="textarea-message" rows={3} value={form.message} onChange={set("message")} placeholder="Fabric type, counts, colours, timelines…" className={`${FIELD} resize-none`} />
          </div>
          <div className="sm:col-span-2">
            <button
              type="submit"
              data-testid="submit-enquiry-btn"
              disabled={sending}
              className="w-full rounded-full bg-crimson py-4 font-body text-sm font-semibold tracking-wide text-parchment transition-[background-color,transform,opacity] duration-300 hover:-translate-y-0.5 hover:bg-crimson-bright disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-12"
            >
              {sending ? "Sending to the Desk…" : "Send Enquiry to the Desk"}
            </button>
            <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.22em] text-clay/70">
              No spam · Your details stay with Praveen Textiles only
            </p>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Contact;
